import { Input, Table, Tag } from "antd";
import useUsers from "hooks/useUsers";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { routesNames } from "utils/Constants";
import { getCurriculumPDF, getPaginacionUsuarios } from "services/Usuarios";
import useNotification from "hooks/useNotification";
import { FaFilePdf } from "react-icons/fa";
import { BiSearchAlt } from "react-icons/bi";
import { BASE_API } from "config/config";
import ExportExcel from "components/excel/ExportExcel";
import dayjs from "dayjs";

const ALL_TYPES = ["I", "P", "A"];

// Tamaño de página usado al traer TODOS los resultados que matchean el filtro
// actual para el botón "Exportar resultados" (no se usa para la tabla en pantalla).
const EXPORT_CHUNK_SIZE = 200;
const EXPORT_MAX_PAGES = 100;

const UsersTable = () => {
    const [value, setValue] = useState("");
    const [datasource, setDatasource] = useState([]);
    const { contextHolder, openNotification } = useNotification();
    const { isLoading, fetchData } = useUsers();
    const [dataExcel, setDataExcel] = useState([]);
    const [sort, setSort] = useState({
        orderBy: "last_login_at",
        orderDirection: "desc",
    });

    // 🆕 "Perfil" (type) se maneja aparte porque además de filtro es el segmentador
    // principal del endpoint paginado — nunca debe quedar vacío/null.
    const [typeFilter, setTypeFilter] = useState([]);

    // 🆕 Filtros que se envían al backend (server-side)
    const [filters, setFilters] = useState({
        estado: [],
        verificado: [],
        plan: [],
        disponible_ia: [],
        perfil_completo: [],
    });

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    /**
     * Función para obtener usuarios con paginación, búsqueda y filtros.
     */
    const fetchUsers = async (
        page = 1,
        pageSize = 20,
        searchValue = "",
        orderBy = sort.orderBy,
        orderDirection = sort.orderDirection,
        filterParams = filters,
        typeParams = typeFilter
    ) => {
        try {
            // "Perfil" vacío = todos los tipos. Siempre truthy para forzar el endpoint paginado.
            const type = typeParams.length > 0 ? typeParams.join(",") : ALL_TYPES.join(",");

            const responseData = await fetchData(null, true, type, page, pageSize, searchValue, orderBy, orderDirection, filterParams);

            if (responseData && responseData?.data && responseData?.pagination) {
                const data = responseData.data;
                const apiPagination = responseData.pagination;

                setDatasource(data);
                dataExport(data);

                setPagination({
                    ...pagination,
                    total: apiPagination.total,
                    current: apiPagination.current_page,
                    pageSize: apiPagination.per_page,
                });
            } else if (responseData && Array.isArray(responseData)) {
                // Caso de fallback si la API retorna el array sin objeto de paginación
                setDatasource(responseData);
                dataExport(responseData);
                setPagination(prev => ({ ...prev, total: responseData.length }));
            } else {
                setDatasource([]);
                setPagination(prev => ({ ...prev, total: 0 }));
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            setDatasource([]);
            setPagination(prev => ({ ...prev, total: 0 }));
        }
    };

    useEffect(() => {
        fetchUsers(pagination.current, pagination.pageSize, value, sort.orderBy, sort.orderDirection, filters, typeFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPdf = async (userId) => {
        const data = await getCurriculumPDF(userId);
        if (data?.uuid) {
            window.open(`${BASE_API}cv/${data.filename}`, "_blank", "noreferrer");
        } else {
            openNotification("El curriculum se encuentra incompleto", "success", "Atención!");
        }
    };

    const buildExcelRows = (data) => {
        if (!data) return [];

        return data.map((item) => {
            return {
                Perfil: item.type === 'I' ? 'Institución' : item.type === 'P' ? 'Postulante' : 'Administrador',
                // Usamos el campo más genérico o combinamos
                Nombre: item.type === 'I' ? item.cuenta?.nombre || item.name : item.name,
                Email: item.email,
                Creado: dayjs(item.created_at).format('DD/MM/YYYY'),
                Plan: item.plan?.detalle?.nombre,
                Estado: item.cuenta?.rechazado ? "Rechazado" : item.cuenta?.suspendido ? "Suspendido" : item.cuenta?.activo ? "Activo" : "Inactivo",
            }
        })
    }

    const dataExport = (data) => setDataExcel(buildExcelRows(data))

    // Trae TODAS las páginas que matchean la búsqueda/filtros actuales (no solo la
    // página visible) para el botón "Exportar resultados".
    const fetchAllFilteredUsers = async () => {
        const type = typeFilter.length > 0 ? typeFilter.join(",") : ALL_TYPES.join(",");
        let page = 1;
        let all = [];
        let total = Infinity;

        while (all.length < total && page <= EXPORT_MAX_PAGES) {
            const response = await getPaginacionUsuarios(type, page, EXPORT_CHUNK_SIZE, value, sort.orderBy, sort.orderDirection, filters);
            const rows = response?.data || [];
            total = response?.pagination?.total ?? rows.length;
            all = all.concat(rows);
            if (rows.length === 0) break;
            page += 1;
        }

        return buildExcelRows(all);
    }

    const hasActiveFilters =
        value.trim().length > 0 ||
        typeFilter.length > 0 ||
        filters.estado.length > 0 ||
        filters.verificado.length > 0 ||
        filters.plan.length > 0 ||
        filters.disponible_ia.length > 0 ||
        filters.perfil_completo.length > 0;

    const getEstado = (record) => {
        if (record.cuenta?.rechazado) return "Rechazado";
        if (record.cuenta?.suspendido) return "Suspendido";
        if (record.cuenta?.activo) return "Activo";
        return "Inactivo";
    };

    const columns = [
        {
            title: "Perfil",
            dataIndex: "type",
            key: "type",
            filters: [
                { text: "Institución", value: "I" },
                { text: "Postulante", value: "P" },
                { text: "Administrador", value: "A" },
            ],
            filteredValue: typeFilter,
            render: (text) => {
                if (text === "I") return <Tag color="lime">Institución</Tag>;
                if (text === "P") return <Tag color="blue">Postulante</Tag>;
                return <Tag color="orange">Administrador</Tag>;
            },
        },
        {
            title: "Nombre",
            dataIndex: "nombre",
            key: "nombre",
            // 🐛 CORREGIDO: Usar el nombre de cuenta para 'I' y el nombre de usuario para otros
            render: (text, record) => (record.type === "I" ? record.cuenta?.nombre : record.name),
        },
        {
            title: "Email",
            dataIndex: "email",
        },
        {
            title: "Creado",
            dataIndex: "created_at",
            render: (text) => new Date(text).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
        {
            title: "Plan",
            dataIndex: "plan",
            render: (text, record) => {
                if (record?.type === "P" || record?.type === "A") return null;
                if (!record?.plan) return <Tag color="purple">STANDARD</Tag>;
                return (
                    <Tag color={record?.plan.plan_id === 1 ? "gold" : "purple"}>
                        {record.plan.detalle.nombre.toUpperCase()}
                    </Tag>
                );
            },
            filters: [
                { text: "STANDARD", value: "Standard" },
                { text: "GOLD", value: "Gold" },
            ],
            filteredValue: filters.plan,
        },
        {
            title: "Verificado",
            dataIndex: "verificado",
            render: (text, record) => (
                <Tag color={record.cuenta?.verificado === 0 ? "red" : "green"}>
                    {record.cuenta?.verificado === 0 ? "Pendiente" : "Verificado"}
                </Tag>
            ),
            filters: [
                { text: "Pendiente", value: 0 },
                { text: "Verificado", value: 1 },
            ],
            filteredValue: filters.verificado,
        },
        {
            title: "Estado",
            dataIndex: "estado",
            render: (text, record) => {
                const estado = getEstado(record);
                return <Tag color={estado === "Activo" ? "green" : "red"}>{estado}</Tag>;
            },
            filters: [
                { text: "Rechazado", value: "Rechazado" },
                { text: "Suspendido", value: "Suspendido" },
                { text: "Activo", value: "Activo" },
                { text: "Inactivo", value: "Inactivo" },
            ],
            filterMultiple: true,
            filteredValue: filters.estado,
        },
        {
            title: "IA",
            dataIndex: "disponible_ia",
            render: (text, record) => (
                <Tag color={record.cuenta?.disponible_ia === -1 ? "green" : "red"}>
                    {record.cuenta?.disponible_ia === -1 ? "Si" : "No"}
                </Tag>
            ),
            filters: [
                { text: "Ver", value: -1 },
            ],
            filteredValue: filters.disponible_ia,
        },
        {
            title: "CV",
            dataIndex: "fecha_perfil_completo",
            render: (text, record) => (
                record?.type === "P" &&
                < Tag color={record?.cuenta?.fecha_perfil_completo !== null ? "green" : "red"} >
                    {record?.cuenta?.fecha_perfil_completo !== null ? "Completo" : "Incompleto"}
                </Tag >
            ),
            filters: [
                { text: "Completo", value: "completo" },
                { text: "Incompleto", value: "incompleto" },
            ],
            filteredValue: filters.perfil_completo,
        },
        {
            title: "Acciones",
            dataIndex: "detalle",
            render: (text, record) => (
                <div className="flex items-center gap-1">
                    <Link to={`${routesNames.USER}/${record.id}`} className="bg-main text-sm px-4 py-1 rounded-lg">
                        Ver
                    </Link>
                    {record?.type === "P" && (
                        record?.cuenta?.fecha_perfil_completo !== null &&
                        <a onClick={() => fetchPdf(record?.id)} className="bg-red-500 text-xs text-white px-2 rounded-md py-1 flex items-center">
                            <FaFilePdf className="text-white" /> Descargar
                        </a>
                    )}
                </div>
            ),
        },
    ];


    return (
        <>
            {contextHolder}
            <Input
                allowClear
                placeholder="Buscar por Nombre, Email, Plan o Tipo de usuario"
                prefix={<BiSearchAlt className="text-xl" />}
                value={value}
                className="p-3 w-full rounded-lg mb-2"
                onChange={(e) => {
                    const currValue = e.target.value;
                    setValue(currValue);

                    fetchUsers(1, pagination.pageSize, currValue, sort.orderBy, sort.orderDirection, filters, typeFilter);

                    setPagination(prev => ({ ...prev, current: 1 }));
                }}
            />

            <div className="flex flex-row justify-between items-center mb-3">
                <span className="block text-sm font-semibold text-start my-1 me-5">
                    {pagination.total} usuarios encontrados
                </span>
                <div className="flex items-center gap-2">
                    {hasActiveFilters && (
                        <ExportExcel data={fetchAllFilteredUsers} fileName={"Usuarios_resultados_" + new Date().toLocaleDateString()} title="Exportar resultados" />
                    )}
                    <ExportExcel data={dataExcel} fileName={"Usuarios_" + new Date().toLocaleDateString()} title="Exportar todo" />
                </div>
            </div>


            <Table
                loading={isLoading}
                dataSource={datasource}
                columns={columns}
                className="w-full"
                rowKey={(record) => record.id}
                size="small"
                pagination={{
                    total: pagination.total,
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    showSizeChanger: false,
                    showQuickJumper: false,
                    showTotal: (total, range) => `Mostrando ${range[0]}-${range[1]} de ${total} usuarios`,
                }}
                locale={{ emptyText: "No hay resultados" }}
                onChange={(newPagination, tableFilters, sorter, extra) => {
                    if (extra.action === "sort") {
                        const sorterField = Array.isArray(sorter) ? sorter?.[0]?.field : sorter?.field;
                        const sorterOrder = Array.isArray(sorter) ? sorter?.[0]?.order : sorter?.order;

                        const nextSort = sorterField && sorterOrder
                            ? {
                                orderBy: sorterField,
                                orderDirection: sorterOrder === "ascend" ? "asc" : "desc",
                            }
                            : {
                                orderBy: "last_login_at",
                                orderDirection: "desc",
                            };

                        setSort(nextSort);
                        setPagination((prev) => ({ ...prev, current: 1 }));
                        fetchUsers(1, pagination.pageSize, value, nextSort.orderBy, nextSort.orderDirection, filters, typeFilter);
                    } else if (extra.action === "paginate" && newPagination.current !== pagination.current) {
                        fetchUsers(newPagination.current, pagination.pageSize, value, sort.orderBy, sort.orderDirection, filters, typeFilter);
                    } else if (extra.action === "filter") {
                        const nextTypeFilter = tableFilters.type || [];
                        const nextFilters = {
                            estado: tableFilters.estado || [],
                            verificado: tableFilters.verificado || [],
                            plan: tableFilters.plan || [],
                            disponible_ia: tableFilters.disponible_ia || [],
                            perfil_completo: tableFilters.fecha_perfil_completo || [],
                        };

                        setTypeFilter(nextTypeFilter);
                        setFilters(nextFilters);
                        setPagination((prev) => ({ ...prev, current: 1 }));
                        fetchUsers(1, pagination.pageSize, value, sort.orderBy, sort.orderDirection, nextFilters, nextTypeFilter);
                    }
                    dataExport(extra.currentDataSource)
                }}
            />

        </>
    );
};

export default UsersTable;
